import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BellRing, Clock } from 'lucide-react';
import * as api from '@/api/supervision.api';
import type { ChatEsperando } from '@/api/supervision.api';

/**
 * EL RECORDATORIO — «tienes clientes esperando».
 *
 * Se le enseña a la asesora, arriba de cualquier pantalla, cuando un chat SUYO lleva
 * más del plazo con el último mensaje del cliente sin respuesta. Desaparece solo
 * cuando contesta. Quien supervisa lo ve en la pantalla de Supervisión, no aquí.
 *
 * ═══ QUE SE OIGA Y SE VEA FUERA DE LA PESTAÑA (12-oct) ═══
 *
 * La franja sola no servía con el panel en otra pestaña o con el teléfono en otra aplicación.
 * Cuando aparece un chat esperando que antes no estaba: suena un tono corto, el título de la
 * pestaña lleva la cuenta («(2) …») y, si la asesora dio permiso, sale una notificación del
 * navegador.
 *
 * ponytail: esto funciona con el panel ABIERTO (aunque esté detrás). Con el navegador cerrado
 * no avisa: eso pide una notificación «push» de verdad (service worker y llaves propias), y
 * en el iPhone además instalar el panel en la pantalla de inicio. Cuando haga falta, aquí.
 *
 * ponytail: pregunta cada minuto en vez de escuchar el tiempo real. Para tres asesoras
 * es una consulta barata y no se puede quedar desincronizada.
 */

function sonar(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const tono = ctx.createOscillator();
    const volumen = ctx.createGain();
    tono.frequency.value = 880;
    volumen.gain.value = 0.08;
    tono.connect(volumen).connect(ctx.destination);
    tono.start();
    tono.stop(ctx.currentTime + 0.25);
    tono.onended = () => void ctx.close();
  } catch {
    /* un navegador que no deja sonar sin un toque previo: la franja y el título siguen */
  }
}

function notificar(chat: ChatEsperando, cuantos: number): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    new Notification(cuantos === 1 ? 'Tienes 1 cliente esperando' : `Tienes ${cuantos} clientes esperando`, {
      body: `${chat.cliente} lleva ${chat.minutos} min sin respuesta.`,
      tag: 'chats-esperando',
    });
  } catch {
    /* Chrome en Android solo deja notificar desde un service worker: se queda en tono y título */
  }
}

const puedePedirPermiso = () => typeof Notification !== 'undefined' && Notification.permission === 'default';

export function RecordatorioDeChats() {
  const { data } = useQuery({
    queryKey: ['supervision', 'esperando'],
    queryFn: api.esperando,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const chats = data?.chats ?? [];
  const yaAvisados = useRef<Set<string>>(new Set());
  const tituloOriginal = useRef(document.title);
  const [pedirPermiso, setPedirPermiso] = useState(puedePedirPermiso);

  const ids = chats.map((c) => c.conversationId).join(',');
  useEffect(() => {
    const nuevos = chats.filter((c) => !yaAvisados.current.has(c.conversationId));
    yaAvisados.current = new Set(chats.map((c) => c.conversationId));
    if (nuevos.length > 0) {
      sonar();
      notificar(nuevos[0]!, chats.length);
    }
    document.title = chats.length > 0 ? `(${chats.length}) ${tituloOriginal.current}` : tituloOriginal.current;
    // `ids` resume la lista: con ella basta para saber si cambió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);
  useEffect(() => {
    const original = tituloOriginal.current;
    return () => {
      document.title = original;
    };
  }, []);

  if (chats.length === 0) return null;

  return (
    <div
      role="alert"
      data-recordatorio
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
    >
      <Clock className="h-4 w-4 shrink-0" />
      <span className="font-medium">
        {chats.length === 1
          ? 'Tienes 1 cliente esperando respuesta:'
          : `Tienes ${chats.length} clientes esperando respuesta:`}
      </span>
      {chats.slice(0, 4).map((c) => (
        <Link
          key={c.conversationId}
          to={`/conversations/${c.conversationId}`}
          className="inline-flex min-h-[32px] items-center rounded-full bg-white/70 px-2.5 underline-offset-2 hover:underline dark:bg-black/20"
        >
          {c.cliente} · {c.minutos} min
        </Link>
      ))}
      {chats.length > 4 && <span>y {chats.length - 4} más</span>}
      {pedirPermiso && (
        <button
          type="button"
          className="ml-auto inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-amber-400 px-3 text-xs font-medium hover:bg-amber-100 dark:border-amber-600 dark:hover:bg-amber-900/50"
          onClick={() => {
            void Notification.requestPermission().then((permiso) => {
              setPedirPermiso(false);
              if (permiso === 'granted') notificar(chats[0]!, chats.length);
            });
          }}
        >
          <BellRing className="h-3.5 w-3.5" /> Avisarme aunque esté en otra pestaña
        </button>
      )}
    </div>
  );
}
